import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { FormLayout } from '../common/index.js';
import loanReceivedService from '../../services/LoanReceivedService.js';
import { Plus, Filter, CheckCircle, ArrowLeft, Pencil, Trash2, MoreVertical, HandCoins, XCircle, CreditCard, Eye, EyeOff, X } from "lucide-react";

const AddRepaymentReceived = () => {
  const { loanId } = useParams();
  const [loan, setLoan] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: '',
    transaction_id: '',
    remarks: ''
  });
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const paymentModes = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'online', label: 'Online Payment' }
  ];

  useEffect(() => {
    fetchLoan();
  }, [loanId]);

  const fetchLoan = async () => {
    try {
      setLoading(true);
      const response = await loanReceivedService.getById(loanId);
      const loanData = response.data;
      
      if (loanData.status !== 'approved' && loanData.status !== 'active') {
        Alert.error('Can only add repayments to approved or active loans');
        navigate('/loans-received');
        return;
      }
      
      setLoan(loanData);
      // Set default amount to pending amount
      setFormData(prev => ({
        ...prev,
        amount: loanData.amount_pending.toString()
      }));
    } catch (err) {
      Alert.error('Failed to load loan data');
      console.error(err);
      navigate('/loans-received');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('File must be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        Alert.error('Only images and PDF files are allowed');
        return;
      }
      
      setPaymentScreenshot(file);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setScreenshotPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setScreenshotPreview('pdf');
      }
    }
  };

  const removeScreenshot = () => {
    setPaymentScreenshot(null);
    setScreenshotPreview(null);
    const fileInput = document.getElementById('screenshot-input');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    const amount = parseFloat(formData.amount);
    
    if (!formData.amount || amount <= 0) {
      Alert.error('Please enter a valid amount');
      return false;
    }
    
    if (amount > loan.amount_pending) {
      Alert.error(`Amount cannot exceed pending amount (${formatCurrency(loan.amount_pending)})`);
      return false;
    }
    
    if (!formData.payment_date) {
      Alert.error('Please select payment date');
      return false;
    }
    
    if (!formData.payment_mode) {
      Alert.error('Please select payment mode');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);

    try {
      await loanReceivedService.addRepayment(loanId, formData, paymentScreenshot);
      Alert.success('Repayment added successfully!');
      setTimeout(() => navigate(`/loans-received/view/${loanId}`), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to add repayment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const setFullPayment = () => {
    setFormData(prev => ({
      ...prev,
      amount: loan.amount_pending.toString()
    }));
  };

  const setPartialPayment = (percentage) => {
    const amount = (loan.amount_pending * percentage / 100).toFixed(2);
    setFormData(prev => ({
      ...prev,
      amount: amount
    }));
  };

  if (loading) {
    return (
      <FormLayout title="Add Repayment" icon={CreditCard} maxWidth="max-w-7xl">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading loan data...</p>
          </div>
        </div>
      </FormLayout>
    );
  }

  if (!loan) return null;

  return (
    <FormLayout
      title="Add Repayment"
      icon={CreditCard}
      onSubmit={handleSubmit}
      submitLabel={submitting ? 'Submitting...' : 'Add Repayment'}
      loading={submitting}
      onCancel={() => navigate('/loans-received')}
      maxWidth="max-w-7xl"
    >

                  {/* Quick Amount Selection */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                      Quick Amount Selection
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={setFullPayment}
                        className="px-3 py-2 bg-green-500 text-white rounded-sm text-sm font-medium hover:bg-green-600 transition-colors"
                      >
                        Full Payment ({formatCurrency(loan.amount_pending)})
                      </button>
                      <button
                        type="button"
                        onClick={() => setPartialPayment(50)}
                        className="px-3 py-2 bg-brand text-white rounded-sm text-sm font-medium hover:bg-brand transition-colors"
                      >
                        50% ({formatCurrency(loan.amount_pending * 0.5)})
                      </button>
                      <button
                        type="button"
                        onClick={() => setPartialPayment(25)}
                        className="px-3 py-2 bg-purple-500 text-white rounded-sm text-sm font-medium hover:bg-purple-600 transition-colors"
                      >
                        25% ({formatCurrency(loan.amount_pending * 0.25)})
                      </button>
                      <button
                        type="button"
                        onClick={() => setPartialPayment(10)}
                        className="px-3 py-2 bg-indigo-500 text-white rounded-sm text-sm font-medium hover:bg-indigo-600 transition-colors"
                      >
                        10% ({formatCurrency(loan.amount_pending * 0.1)})
                      </button>
                    </div>
                  </div>

                  {/* Payment Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Payment Details
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Payment Amount *
                        </label>
                        <input
                          type="number"
                          name="amount"
                          value={formData.amount}
                          onChange={handleChange}
                          placeholder="Enter payment amount"
                          step="0.01"
                          min="0.01"
                          max={loan.amount_pending}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          Maximum: {formatCurrency(loan.amount_pending)}
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Payment Date *
                        </label>
                        <input
                          type="date"
                          name="payment_date"
                          value={formData.payment_date}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Payment Mode *
                        </label>
                        <select
                          name="payment_mode"
                          value={formData.payment_mode}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Payment Mode</option>
                          {paymentModes.map((mode) => (
                            <option key={mode.value} value={mode.value}>
                              {mode.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Transaction ID / Reference Number
                        </label>
                        <input
                          type="text"
                          name="transaction_id"
                          value={formData.transaction_id}
                          onChange={handleChange}
                          placeholder="Enter transaction ID"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment Screenshot */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Payment Proof
                    </h3>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                        Payment Screenshot / Receipt
                      </label>
                      <input
                        id="screenshot-input"
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={handleScreenshotChange}
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Upload payment screenshot or receipt (Max 5MB)
                      </p>
                      
                      {screenshotPreview && (
                        <div className="mt-3">
                          {screenshotPreview === 'pdf' ? (
                            <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-sm">
                              <span className="text-2xl">📄</span>
                              <span className="text-sm text-gray-700">{paymentScreenshot.name}</span>
                              <button
                                type="button"
                                onClick={removeScreenshot}
                                className="ml-auto text-red-500 hover:text-red-600"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="relative inline-block">
                              <img 
                                src={screenshotPreview} 
                                alt="Payment Screenshot" 
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border-2 border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeScreenshot}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Remarks */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Additional Information
                    </h3>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                        Remarks / Notes
                      </label>
                      <textarea
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        placeholder="Enter any additional notes about this payment"
                        rows="3"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                      />
                    </div>
                  </div>

                  {/* Summary Box */}
                  <div className="bg-brand-light border-l-4 border-brand p-4 rounded">
                    <h4 className="font-semibold text-blue-900 mb-2">Payment Summary</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-blue-800">Payment Amount:</span>
                        <span className="font-semibold text-blue-900">
                          {formatCurrency(parseFloat(formData.amount) || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-800">Current Pending:</span>
                        <span className="font-semibold text-blue-900">{formatCurrency(loan.amount_pending)}</span>
                      </div>
                      <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
                        <span className="text-blue-800 font-semibold">Remaining After Payment:</span>
                        <span className="font-bold text-blue-900">
                          {formatCurrency(Math.max(0, loan.amount_pending - (parseFloat(formData.amount) || 0)))}
                        </span>
                      </div>
                    </div>
                  </div>

    </FormLayout>
  );
};

export default AddRepaymentReceived;